import { Button } from '@kognitos/lattice';
import {
  Bdkv2OAuthProvider,
  type Bdkv2BookAuthenticationDescriptor,
  type Bdkv2BookDescriptor,
} from '@/generated/hooks/bdk/models';
import { useTranslation } from 'react-i18next';

interface IOAuthProviderConfig {
  name: string;
  icon: string | undefined;
}

const GOOGLE_ICON =
  'PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjUiIHZpZXdCb3g9IjAgMCAyNCAyNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIxLjQ5MzIgMTIuNzY0NUMyMS40OTMyIDEyLjA2MzQgMjEuNDMwMyAxMS4zODk0IDIxLjMxMzUgMTAuNzQyMkgxMi4wMDJWMTQuNTY2NkgxNy4zMjI4QzE3LjA5MzYgMTUuODAyNCAxNi4zOTcxIDE2Ljg0OTUgMTUuMzUgMTcuNTUwNlYyMC4wMzEzSDE4LjU0NTJDMjAuNDE0NyAxOC4zMTAxIDIxLjQ5MzIgMTUuNzc1NCAyMS40OTMyIDEyLjc2NDVaIiBmaWxsPSIjNDI4NUY0Ii8+CjxwYXRoIGQ9Ik0xMS45OTkyIDIyLjQzMDFDMTQuNjY4NiAyMi40MzAxIDE2LjkwNjYgMjEuNTQ0OCAxOC41NDI0IDIwLjAzNDlMMTUuMzQ3MiAxNy41NTQyQzE0LjQ2MTkgMTguMTQ3NCAxMy4zMjk0IDE4LjQ5NzkgMTEuOTk5MiAxOC40OTc5QzkuNDI0MTQgMTguNDk3OSA3LjI0NDYxIDE2Ljc1ODcgNi40NjcxMiAxNC40MjE5SDMuMTY0MDZWMTYuOTgzNEM0Ljc5MDkyIDIwLjIxNDYgOC4xMzQ0MSAyMi40MzAxIDExLjk5OTIgMjIuNDMwMVoiIGZpbGw9IiMzNEE4NTMiLz4KPHBhdGggZD0iTTYuNDY3ODkgMTQuNDIyQzYuMjcwMTUgMTMuODI4OCA2LjE1Nzg0IDEzLjE5NTIgNi4xNTc4NCAxMi41NDM1QzYuMTU3ODQgMTEuODkxOSA2LjI3MDE1IDExLjI1ODMgNi40Njc4OSAxMC42NjUxVjguMTAzNTJIMy4xNjQ4M0MyLjQ5NTMgOS40MzgyMiAyLjExMzI4IDEwLjk0ODIgMi4xMTMyOCAxMi41NDM1QzIuMTEzMjggMTQuMTM4OSAyLjQ5NTMgMTUuNjQ4OSAzLjE2NDgzIDE2Ljk4MzZMNi40Njc4OSAxNC40MjJaIiBmaWxsPSIjRkJCQzA0Ii8+CjxwYXRoIGQ9Ik0xMS45OTkyIDYuNTg2NTRDMTMuNDUwNyA2LjU4NjU0IDE0Ljc1NCA3LjA4NTMzIDE1Ljc3ODYgOC4wNjVMMTguNjE0MyA1LjIyOTI5QzE2LjkwMjIgMy42MzM5NyAxNC42NjQxIDIuNjU0MyAxMS45OTkyIDIuNjU0M0M4LjEzNDQxIDIuNjU0MyA0Ljc5MDkyIDQuODY5ODEgMy4xNjQwNiA4LjEwMDk5TDYuNDY3MTIgMTAuNjYyNUM3LjI0NDYxIDguMzI1NzEgOS40MjQxNCA2LjU4NjU0IDExLjk5OTIgNi41ODY1NFoiIGZpbGw9IiNFOTQyMzUiLz4KPC9zdmc+';

const MICROSOFT_ICON =
  'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSIgdmlld0JveD0iMCAwIDIxIDIxIj48dGl0bGU+TVMtU3ltYm9sTG9ja3VwPC90aXRsZT48cmVjdCB4PSIxIiB5PSIxIiB3aWR0aD0iOSIgaGVpZ2h0PSI5IiBmaWxsPSIjZjI1MDIyIi8+PHJlY3QgeD0iMSIgeT0iMTEiIHdpZHRoPSI5IiBoZWlnaHQ9IjkiIGZpbGw9IiMwMGE0ZWYiLz48cmVjdCB4PSIxMSIgeT0iMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iIzdmYmEwMCIvPjxyZWN0IHg9IjExIiB5PSIxMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iI2ZmYjkwMCIvPjwvc3ZnPg==';

/**
 * The provider name and icon shown for an OAuth auth method's sign-in.
 *
 * @param integration - The book descriptor (fallback name and icon).
 * @param authentication - The OAuth auth method.
 * @returns The provider's display name and icon.
 */
export const getOAuthProviderConfig = (
  integration: Bdkv2BookDescriptor,
  authentication: Bdkv2BookAuthenticationDescriptor,
): IOAuthProviderConfig => {
  switch (authentication.oauth?.provider) {
    case Bdkv2OAuthProvider.OAuthProviderGoogle:
      return { name: 'Google', icon: GOOGLE_ICON };
    case Bdkv2OAuthProvider.OAuthProviderMicrosoft:
      return { name: 'Microsoft', icon: MICROSOFT_ICON };
    default:
      return {
        name: integration.display_name || integration.name || '',
        icon: integration.icon,
      };
  }
};

export interface OAuthSignInButtonProps {
  integration: Bdkv2BookDescriptor;
  authentication: Bdkv2BookAuthenticationDescriptor;
  disabled: boolean;
  onClick?: () => void;
  isLoading?: boolean;
  /**
   * Overrides the button label with the connection's own display name.
   * Set when several managed connections share the same provider so they can
   * be told apart; otherwise the button falls back to "Sign in with <Provider>".
   */
  displayName?: string;
}

export function OAuthSignInButton({
  integration,
  authentication,
  disabled,
  onClick,
  isLoading = false,
  displayName,
}: OAuthSignInButtonProps) {
  const { t: tIntegrations } = useTranslation('integrations');

  const oAuthProviderConfig = getOAuthProviderConfig(
    integration,
    authentication,
  );

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      disabled={disabled || isLoading}
      onClick={onClick}
      isLoading={isLoading}
    >
      <img
        src={
          oAuthProviderConfig.icon?.startsWith('data:')
            ? oAuthProviderConfig.icon
            : `data:image/svg+xml;base64,${oAuthProviderConfig.icon}`
        }
        alt={oAuthProviderConfig.name}
        className="w-4 h-4"
      />
      {displayName ||
        tIntegrations('integrations.oauth.signInButton', {
          oauthProvider: oAuthProviderConfig.name,
        })}
    </Button>
  );
}
